import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react'

import { useShortcuts } from '@elsapiens/providers'
import { clsx } from 'clsx'
import { createPortal } from 'react-dom'


import { Button, type ButtonVariant } from './button'

/**
 * Modal size variants
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback when modal should close */
  onClose: () => void
  /** Modal title */
  title?: string
  /** Modal description (below title) */
  description?: string
  /** Modal size */
  size?: ModalSize
  /** Whether clicking outside closes the modal */
  closeOnOutsideClick?: boolean
  /** Whether pressing Escape closes the modal */
  closeOnEscape?: boolean
  /** Whether to show the close button */
  showCloseButton?: boolean
  /** Modal content */
  children: ReactNode
  /** Footer content (typically buttons) */
  footer?: ReactNode
  /** Extra classes applied to the scrollable body div (overrides default el-p-xl) */
  bodyClassName?: string
  /**
   * When true, the first Escape press shows a "press Escape again to close" warning
   * instead of closing immediately. A second Escape press closes the modal.
   */
  isDirty?: boolean
}

const sizeStyles: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  full: 'max-w-[800px]',
}

// ============================================================================
// Sub-components
// ============================================================================

interface ModalHeaderProps {
  title?: string
  description?: string
  showCloseButton: boolean
  onClose: () => void
}

function ModalHeader({ title, description, showCloseButton, onClose }: ModalHeaderProps): JSX.Element | null {
  if (!title && !showCloseButton) { return null }
  return (
    <div className="flex items-start justify-between el-p-xl border-b border-border bg-card rounded-t-lg">
      <div>
        {title && <h2 className="text-lg font-semibold text-foreground" id="modal-title">{title}</h2>}
        {description && <p className="mt-1 text-sm text-muted-foreground" id="modal-description">{description}</p>}
      </div>
      {showCloseButton && (
        <button
          aria-label="Close modal"
          className={clsx('p-2 rounded-md -m-2', 'text-muted-foreground hover:text-foreground', 'hover:bg-accent transition-colors duration-150')}
          type="button"
          onClick={onClose}
        >
          <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          </svg>
        </button>
      )}
    </div>
  )
}

interface ModalFooterProps {
  footer: ReactNode
}

function ModalFooter({ footer }: ModalFooterProps): JSX.Element {
  return (
    <div className="el-p-xl border-t border-border bg-card rounded-b-lg">
      <div className="el-gap-control-md flex items-center justify-end">
        {footer}
      </div>
    </div>
  )
}

function DirtyWarningBanner(): JSX.Element {
  return (
    <div className="flex items-center gap-2 px-6 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-sm">
      <svg aria-hidden="true" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      </svg>
      <span>You have unsaved changes — press <kbd className="font-mono font-semibold">Esc</kbd> again to discard and close.</span>
    </div>
  )
}

// ============================================================================
// Focus management hook
// ============================================================================

function useModalFocusTrap(isOpen: boolean, modalRef: React.RefObject<HTMLDivElement>): void {
  useEffect(() => {
    if (!isOpen) { return }
    const previousActiveElement = document.activeElement as HTMLElement
    const focusableElements = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusableElements && focusableElements.length > 0) {
      (focusableElements[0] as HTMLElement).focus()
    }
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
      previousActiveElement?.focus()
    }
  }, [isOpen, modalRef])
}

// ============================================================================
// Inner panel
// ============================================================================

interface ModalInnerPanelProps {
  modalRef: React.RefObject<HTMLDivElement>
  size: ModalSize
  title?: string
  description?: string
  showCloseButton: boolean
  footer?: ReactNode
  children: ReactNode
  onClose: () => void
  onOverlayClick: (e: React.MouseEvent) => void
  bodyClassName?: string
  shouldWarnClose: boolean
}

function ModalInnerPanel({
  modalRef, size, title, description, showCloseButton, footer, children, onClose, onOverlayClick, bodyClassName, shouldWarnClose,
}: ModalInnerPanelProps): JSX.Element {
  const handlePositionerKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') { onOverlayClick(e as unknown as React.MouseEvent) }
  }

  return (
    <div
      className="flex min-h-full max-h-screen items-center justify-center p-4"
      role="presentation"
      onClick={onOverlayClick}
      onKeyDown={handlePositionerKeyDown}
    >
      <div
        ref={modalRef}
        className={clsx(
          'relative w-full rounded-lg shadow-xl bg-card border border-border',
          'animate-scale-in max-h-[98vh] flex flex-col',
          sizeStyles[size]
        )}
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <ModalHeader description={description} showCloseButton={showCloseButton} title={title} onClose={onClose} />
        <div className={clsx('overflow-y-auto flex-1 min-h-0', bodyClassName ?? 'el-p-xl')}>{children}</div>
        {shouldWarnClose && <DirtyWarningBanner />}
        {footer && <ModalFooter footer={footer} />}
      </div>
    </div>
  )
}

// ============================================================================
// Overlay backdrop
// ============================================================================

interface OverlayBackdropProps {
  onOverlayClick: (event: React.MouseEvent) => void
}

function OverlayBackdrop({ onOverlayClick }: OverlayBackdropProps): JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={clsx('fixed inset-0 bg-black/50', 'animate-fade-in')}
      role="presentation"
      onClick={onOverlayClick}
      onKeyDown={(e) => { if (e.key === 'Escape') { onOverlayClick(e as unknown as React.MouseEvent) } }}
    />
  )
}

// ============================================================================
// Modal dialog root (accessibility wrapper)
// ============================================================================

interface ModalDialogRootProps {
  children: ReactNode
  description?: string
  title?: string
}

function ModalDialogRoot({ children, description, title }: ModalDialogRootProps): JSX.Element {
  return (
    <div
      aria-describedby={description ? 'modal-description' : undefined}
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-modal="true"
      className="fixed inset-0 z-50"
      role="dialog"
    >
      {children}
    </div>
  )
}

// ============================================================================
// Modal
// ============================================================================

/**
 * Modal component following elBooks UI/UX standards
 *
 * Features:
 * - Focus trap for accessibility
 * - Escape key to close (works even when focus is in form inputs)
 * - Dirty-form guard: pass isDirty=true to require a second Escape press with a warning
 * - Click outside to close (optional)
 * - Animated entrance/exit
 * - Multiple size variants
 */
interface UseWarnCloseInput {
  isOpen: boolean
  isCloseOnEscape: boolean
  isDirty: boolean
  onClose: () => void
}

interface UseWarnCloseResult {
  shouldWarnClose: boolean
  shouldWarnCloseRef: React.MutableRefObject<boolean>
  setShouldWarnClose: React.Dispatch<React.SetStateAction<boolean>>
}

function useWarnAndEscape(input: UseWarnCloseInput): UseWarnCloseResult {
  const { isOpen, isCloseOnEscape, isDirty, onClose } = input
  const [shouldWarnClose, setShouldWarnClose] = useState(false)
  const shouldWarnCloseRef = useRef(false)
  useEffect(() => { shouldWarnCloseRef.current = shouldWarnClose }, [shouldWarnClose])
  useEffect(() => {
    if (!shouldWarnClose) { return undefined }
    const t = setTimeout(() => setShouldWarnClose(false), 3000)
    return () => clearTimeout(t)
  }, [shouldWarnClose])
  useEffect(() => {
    if (!isOpen) { setShouldWarnClose(false) }
  }, [isOpen])
  useEffect(() => {
    if (!isOpen || !isCloseOnEscape) { return undefined }
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') { return }
      if (isDirty && !shouldWarnCloseRef.current) {
        setShouldWarnClose(true)
        return
      }
      setShouldWarnClose(false)
      onClose()
    }
    document.addEventListener('keydown', handleEscape, { capture: true })
    return () => document.removeEventListener('keydown', handleEscape, { capture: true })
  }, [isOpen, isCloseOnEscape, isDirty, onClose])
  return { shouldWarnClose, shouldWarnCloseRef, setShouldWarnClose }
}

function useModalShortcutContext(isOpen: boolean): void {
  const { pushContext, popContext } = useShortcuts()
  useEffect(() => {
    if (!isOpen) { return undefined }
    pushContext('modal')
    return () => { popContext('modal') }
  }, [isOpen, pushContext, popContext])
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  closeOnOutsideClick: isCloseOnOutsideClick = true,
  closeOnEscape: isCloseOnEscape = true,
  showCloseButton: isShowCloseButton = true,
  children,
  footer,
  bodyClassName,
  isDirty = false,
}: ModalProps): JSX.Element | null {
  const modalRef = useRef<HTMLDivElement>(null)
  useModalShortcutContext(isOpen)
  const { shouldWarnClose } = useWarnAndEscape({ isOpen, isCloseOnEscape, isDirty, onClose })
  useModalFocusTrap(isOpen, modalRef)

  const handleOverlayClick = useCallback((event: React.MouseEvent): void => {
    if (isCloseOnOutsideClick && event.target === event.currentTarget) { onClose() }
  }, [isCloseOnOutsideClick, onClose])

  if (!isOpen) { return null }

  return createPortal(
    <ModalDialogRoot description={description} title={title}>
      <OverlayBackdrop onOverlayClick={handleOverlayClick} />
      <ModalInnerPanel
        bodyClassName={bodyClassName}
        description={description}
        footer={footer}
        modalRef={modalRef}
        shouldWarnClose={shouldWarnClose}
        showCloseButton={isShowCloseButton}
        size={size}
        title={title}
        onClose={onClose}
        onOverlayClick={handleOverlayClick}
      >
        {children}
      </ModalInnerPanel>
    </ModalDialogRoot>,
    document.body
  )
}

// ============================================================================
// Confirmation Dialog body sub-components
// ============================================================================

interface ConfirmDetailsProps {
  details: string[]
}

function ConfirmDetails({ details }: ConfirmDetailsProps): JSX.Element {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-muted-foreground">This will:</p>
      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
        {details.map((detail, index) => <li key={index}>{detail}</li>)}
      </ul>
    </div>
  )
}

interface ConfirmDialogFooterButtonsProps {
  cancelText: string
  confirmText: string
  confirmVariant: ButtonVariant
  isLoading: boolean
  onClose: () => void
  onConfirm: () => void
}

function ConfirmDialogFooterButtons({
  cancelText, confirmText, confirmVariant, isLoading, onClose, onConfirm,
}: ConfirmDialogFooterButtonsProps): JSX.Element {
  return (
    <>
      <Button disabled={isLoading} size="sm" variant="ghost" onClick={onClose}>{cancelText}</Button>
      <Button isLoading={isLoading} loadingText="Processing..." size="sm" variant={confirmVariant} onClick={onConfirm}>
        {confirmText}
      </Button>
    </>
  )
}

// ============================================================================
// Confirmation Dialog
// ============================================================================

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** Callback when dialog should close */
  onClose: () => void
  /** Callback when action is confirmed */
  onConfirm: () => void
  /** Dialog title */
  title: string
  /** Dialog message */
  message: string
  /** Additional details (bullet points) */
  details?: string[]
  /** Warning text (e.g., "This action cannot be undone.") */
  warning?: string
  /** Confirm button text */
  confirmText?: string
  /** Cancel button text */
  cancelText?: string
  /** Confirm button variant */
  confirmVariant?: ButtonVariant
  /** Whether the confirm action is loading */
  isLoading?: boolean
  /** Optional extra content rendered below the message */
  children?: ReactNode
}

/**
 * Confirmation dialog for destructive or important actions
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  details,
  warning,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  isLoading = false,
  children,
}: ConfirmDialogProps): JSX.Element {
  return (
    <Modal
      closeOnEscape={!isLoading}
      closeOnOutsideClick={!isLoading}
      footer={
        <ConfirmDialogFooterButtons
          cancelText={cancelText}
          confirmText={confirmText}
          confirmVariant={confirmVariant}
          isLoading={isLoading}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      }
      isOpen={isOpen}
      size="sm"
      title={title}
      onClose={onClose}
    >
      <div className="space-y-4">
        <p className="text-muted-foreground">{message}</p>
        {details && details.length > 0 && <ConfirmDetails details={details} />}
        {warning && <p className="text-sm font-medium text-red-600 dark:text-red-400">{warning}</p>}
        {children}
      </div>
    </Modal>
  )
}

export default Modal
